/**
 * WP-V2-CS — katalog obiektów Szkoły (PLAN-V2 §6 School).
 */
import type { ObjectDef, SnapZone, SpawnPlacement } from '../../interact/index.js';
import type { ColliderRect } from '../../core/world/index.js';

export const SCHOOL_WORLD_W = 1600;
export const SCHOOL_WORLD_H = 720;

export const SCHOOL_BLOCK_DEF_IDS = [
  'school_block_a',
  'school_block_b',
  'school_block_c',
  'school_block_d',
] as const;

export const SCHOOL_QUEST_ID = 'school_blocks';
export const SCHOOL_SHELF_SNAP = 'school_shelf';
export const SCHOOL_SHELF_SLOT_IDS = [
  'school_shelf_a',
  'school_shelf_b',
  'school_shelf_c',
  'school_shelf_d',
] as const;
export type SchoolShelfSlotId = (typeof SCHOOL_SHELF_SLOT_IDS)[number];

export function shelfSlotForBlock(defId: string): SchoolShelfSlotId | null {
  const match = /^school_block_([a-d])$/.exec(defId);
  return match ? (`school_shelf_${match[1]}` as SchoolShelfSlotId) : null;
}

export const SCHOOL_OBJECT_DEFS: ObjectDef[] = [
  {
    id: 'school_backpack',
    texture: 'obj_school_backpack',
    size: 96,
    interactions: ['tap', 'toggle'],
    states: ['closed', 'open'],
    fx: 'stars',
    dogReaction: 'happy',
  },
  {
    id: 'school_crayon',
    texture: 'obj_school_crayon',
    size: 68,
    interactions: ['pickup', 'drag', 'give'],
    fx: 'sparkle',
    dogReaction: 'sniff',
    snapTargets: ['school_desk'],
  },
  {
    id: 'school_eraser',
    texture: 'obj_school_eraser',
    size: 68,
    interactions: ['pickup', 'drag'],
    fx: 'sparkle',
    snapTargets: [],
  },
  {
    id: 'school_block_a',
    texture: 'obj_school_block_a',
    size: 72,
    interactions: ['drag', 'pickup'],
    fx: 'stars',
    dogReaction: 'happy',
    snapTargets: ['school_shelf_a'],
  },
  {
    id: 'school_block_b',
    texture: 'obj_school_block_b',
    size: 72,
    interactions: ['drag', 'pickup'],
    fx: 'stars',
    dogReaction: 'happy',
    snapTargets: ['school_shelf_b'],
  },
  {
    id: 'school_block_c',
    texture: 'obj_school_block_c',
    size: 72,
    interactions: ['drag', 'pickup'],
    fx: 'stars',
    dogReaction: 'happy',
    snapTargets: ['school_shelf_c'],
  },
  {
    id: 'school_block_d',
    texture: 'obj_school_block_d',
    size: 72,
    interactions: ['drag', 'pickup'],
    fx: 'stars',
    dogReaction: 'happy',
    snapTargets: ['school_shelf_d'],
  },
  {
    id: 'school_bell',
    texture: 'obj_school_bell',
    size: 80,
    interactions: ['tap'],
    sound: 'ding',
    fx: 'notes',
    dogReaction: 'jump',
  },
  {
    id: 'school_desk_a',
    texture: 'obj_school_desk',
    size: 96,
    interactions: ['tap'],
    fx: 'hearts',
    // brak dogReaction celowo: SchoolScene.onInteract odpala playAction('sit')
    // dla ław; ustawienie tu dogReaction nadpisywałoby 'sit' natychmiastową
    // reakcją 'happy' z ObjectLayer.playDogReaction, zanim scena zdąży
    // wyrenderować siadanie (wyścig dwóch playAction w tym samym ticku).
  },
  {
    id: 'school_desk_b',
    texture: 'obj_school_desk',
    size: 96,
    interactions: ['tap'],
    fx: 'hearts',
    // j.w. — patrz school_desk_a.
  },
  {
    id: 'school_globe',
    texture: 'obj_school_globe',
    size: 72,
    interactions: ['tap'],
    fx: 'sparkle',
    dogReaction: 'sniff',
  },
];

export const SCHOOL_PLACEMENTS: SpawnPlacement[] = [
  { defId: 'school_backpack', x: 220, y: 480 },
  { defId: 'school_crayon', x: 280, y: 500 },
  { defId: 'school_eraser', x: 340, y: 500 },
  // klocki liter (drag → półka)
  { defId: 'school_block_a', x: 700, y: 520 },
  { defId: 'school_block_b', x: 780, y: 530 },
  { defId: 'school_block_c', x: 860, y: 520 },
  { defId: 'school_block_d', x: 940, y: 530 },
  { defId: 'school_bell', x: 1400, y: 280 },
  { defId: 'school_desk_a', x: 420, y: 440 },
  { defId: 'school_desk_b', x: 620, y: 440 },
  { defId: 'school_globe', x: 1480, y: 420 },
];

export const SCHOOL_SNAP_ZONES: SnapZone[] = [
  { id: 'school_shelf_a', x: 1010, y: 220, radius: 38 },
  { id: 'school_shelf_b', x: 1070, y: 220, radius: 38 },
  { id: 'school_shelf_c', x: 1130, y: 220, radius: 38 },
  { id: 'school_shelf_d', x: 1190, y: 220, radius: 38 },
  { id: 'school_desk', x: 520, y: 440, radius: 60 },
];

export const SCHOOL_COLLIDERS: ColliderRect[] = [
  { x: 0, y: 0, width: SCHOOL_WORLD_W, height: 90 },
  { x: 400, y: 120, width: 700, height: 30 }, // tablica (obrzeże)
];

export function isLetterBlockDef(defId: string): boolean {
  return (SCHOOL_BLOCK_DEF_IDS as readonly string[]).includes(defId);
}

export function countLetterBlockPlacements(
  placements: readonly SpawnPlacement[] = SCHOOL_PLACEMENTS,
): number {
  return placements.filter((p) => isLetterBlockDef(p.defId)).length;
}
